import { useEffect, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useLayoutStore } from '@common/core/stores'

import { fetchLibraries } from '@/core/api'
import { useLibraryStore } from '@/core/stores/librariesStore'
import { getGatewayConfig } from '@/core/utils/gateway'
import { isEmptyLibrariesQuery } from '@/core/utils/isEmptyLibrariesQuery'
import { isLocalGatewayConfigurationMode } from '@/core/utils/isLocalGatewayConfigurationMode'
import { mapLibrary } from '@/core/utils/mappers/mapLibrary'
import { mergeGatewayUserSettings } from '@/core/utils/mergeGatewayUserSettings'
import { serializeGatewayUserSettingsKey } from '@/core/utils/serializeGatewayUserSettingsKey'
import { syncGatewayRuntimeToLibraryStore } from '@/core/utils/syncGatewayRuntimeToLibraryStore'

import { useGatewayUserSettings } from './useGatewayUserSettings'

export const LIBRARIES_QUERY_KEY = ['libraries'] as const

const formatGatewayDisplayName = (host: string, port?: string): string => {
  if (!host) {
    return ''
  }

  return port ? `${host}:${port}` : host
}

export function useLibraries() {
  const { host: baseHost, port: basePort, isSecured: baseIsSecured } = getGatewayConfig()
  const { gatewaySettings } = useGatewayUserSettings()
  const config = useMemo(
    () => mergeGatewayUserSettings({ host: baseHost, port: basePort, isSecured: baseIsSecured }, gatewaySettings),
    [baseHost, basePort, baseIsSecured, gatewaySettings]
  )
  const state = useLibraryStore()
  const { startLoader, stopLoader, clearFullScreenLoader } = useLayoutStore()
  const settingsKey = serializeGatewayUserSettingsKey(gatewaySettings)

  const query = useQuery({
    queryKey: [...LIBRARIES_QUERY_KEY, config.host, config.port, settingsKey],
    queryFn: () => fetchLibraries(config),
    enabled: !!config.host,
    retry: gatewaySettings.useCustom ? 0 : 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const websocketPort = query.data?.websocketPort ?? ''
  const mcpPort = query.data?.mcpPort ?? ''

  // Show global loader only for the initial fetch of the current query key.
  useEffect(() => {
    if (query.isLoading) {
      startLoader()
      return () => {
        stopLoader()
      }
    }

    // Release the layout store's initial loader (loaderCount starts at 1 in Vision).
    clearFullScreenLoader()
    return undefined
  }, [clearFullScreenLoader, query.isLoading, startLoader, stopLoader])

  useEffect(() => {
    if (!isLocalGatewayConfigurationMode()) {
      return
    }

    syncGatewayRuntimeToLibraryStore({
      config,
      apiPorts: { websocketPort, mcpPort },
      gatewaySettings,
    })
  }, [config, gatewaySettings, mcpPort, websocketPort])

  // Sync query data to Zustand store
  useEffect(() => {
    if (!query.data) {
      return
    }

    const mappedLibraries = query.data.libraries.reduce<Record<string, ReturnType<typeof mapLibrary>>>((acc, lib) => {
      const mapped = mapLibrary(lib)
      acc[mapped.id] = mapped
      return acc
    }, {})

    syncGatewayRuntimeToLibraryStore({
      config,
      apiPorts: {
        websocketPort: query.data.websocketPort,
        mcpPort: query.data.mcpPort,
      },
      gatewaySettings,
      extraState: {
        libraries: mappedLibraries,
        guid: query.data.guid,
        projectName: query.data.projectName,
        registry: query.data.registry,
        gsmuEndpoint: query.data.gsmuEndpoint,
      },
    })
  }, [query.data, config, gatewaySettings])

  return {
    libraries: state.libraries,
    gateway: state.gateway,
    gatewayPath: state.gatewayPath,
    gatewayPort: state.gatewayPort,
    gatewayDisplayName: formatGatewayDisplayName(config.host, config.port),
    gatewayWsPort: state.gatewayWsPort,
    gatewayMcpPort: state.gatewayMcpPort,
    isSecured: state.isSecured,
    guid: state.guid,
    registry: state.registry,
    projectName: state.projectName,
    gsmuEndpoint: state.gsmuEndpoint,
    isLoading: query.isPending,
    isError: query.isError,
    hasNoModules: isEmptyLibrariesQuery(query.isSuccess, query.data?.libraries.length ?? 0),
    error: query.error,
    refetch: query.refetch,
  }
}
